import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { HelplineBox } from '../components/GuideContent';
import WellbeingWheel from '../components/dashboard/WellbeingWheel';
import { Slider } from '../components/ui/slider';
import { Progress } from '../components/ui/progress';
import { ghostBtn, primaryBtn } from '../components/dashboard/ui';
import api from '../lib/api';
import { logError } from '../lib/log';
import { useAuth } from '../context/AuthContext';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  QUESTIONS, RECOMMENDATIONS, RESULT, SPOKES,
  challengeById, computePriorities, emptyWheel, isAnswered,
  needsSupport, wantsConditionGuides,
} from '../lib/assessment';

const optionBtn = (active) => `w-full text-left p-space-md rounded-lg border transition-colors font-body-md text-body-md ${
  active
    ? 'bg-primary-container border-surface-tint text-outline font-semibold'
    : 'bg-surface border-surface-container text-on-surface-variant hover:border-surface-tint'
}`;

const GuideLinks = ({ slugs, titles, testId }) => (
  <div className="flex flex-col gap-space-xs" data-testid={testId}>
    {slugs.map((slug) => (
      <Link
        key={slug}
        to={`/topics/${slug}`}
        className="flex items-center justify-between p-space-md rounded-lg bg-surface-container-low hover:bg-primary-container transition-colors group"
      >
        <span className="font-body-md text-body-md text-outline group-hover:text-surface-tint transition-colors">{titles[slug] || slug}</span>
        <Icon name="chevron_right" className="text-outline group-hover:text-surface-tint transition-colors shrink-0" />
      </Link>
    ))}
  </div>
);

export default function StartAssessment() {
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const isAuthed = user && typeof user === 'object';
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ q1: emptyWheel() });
  const [done, setDone] = useState(false);
  const [titles, setTitles] = useState({});
  const [consent, setConsent] = useState(false);
  const [mode, setMode] = useState('signup'); // signup | login
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [saved, setSaved] = useState(false);

  usePageMeta({
    title: 'Start your wellbeing | OVERALL WELLBEING',
    description: 'A few simple questions to show you where to start. Not a medical assessment.',
    path: '/start',
    noindex: true,
  });

  const question = QUESTIONS[step];
  const priorities = useMemo(() => (done ? computePriorities(answers) : []), [done, answers]);

  useEffect(() => {
    if (!done) return;
    (async () => {
      try {
        const { data } = await api.get('/knowledge/published-guides');
        setTitles((data.items || []).reduce((acc, t) => ({ ...acc, [t.slug]: t.display_title || t.title }), {}));
      } catch (e) { logError('StartAssessment.titles', e); }
    })();
  }, [done]);

  const setAnswer = (value) => setAnswers((a) => ({ ...a, [question.id]: value }));

  const toggleMulti = (option) => {
    const current = answers[question.id] || [];
    if (current.includes(option)) {
      setAnswer(current.filter((o) => o !== option));
    } else if (current.length < question.max) {
      setAnswer([...current, option]);
    }
  };

  const next = () => {
    if (step + 1 < QUESTIONS.length) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const savePlan = async (event) => {
    event.preventDefault();
    setBusy(true);
    setNotice('');
    const starter = RECOMMENDATIONS[priorities[0]]?.starter_habit;
    try {
      if (!isAuthed) {
        if (mode === 'signup') {
          await register(form.name, form.email, form.password, consent ? starter : null);
        } else {
          await login(form.email, form.password);
        }
      }
      if (consent) {
        await api.post('/me/wellbeing/assessment', {
          answers,
          wheel: answers.q1,
          priorities,
          consent: true,
        });
        if (starter) {
          await api.post('/me/habits', { title: starter, source_topic_id: null });
        }
      }
      setSaved(true);
      navigate('/my-login');
    } catch (e) {
      logError('StartAssessment.save', e);
      setNotice(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ------------------------------ result ---------------------------------- //
  if (done) {
    return (
      <div className="flex flex-col w-full" data-testid="assessment-result">
        <section className="w-full px-margin-mobile lg:px-margin pt-space-xl pb-space-2xl bg-surface relative overflow-hidden">
          <div className="absolute top-0 right-1/4 w-[420px] h-[420px] rounded-full bg-primary-container/50 blur-[120px] pointer-events-none -z-10" />
          <div className="max-w-4xl mx-auto">
            <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block mb-space-xs">YOUR WELLBEING SNAPSHOT</span>
            <h1 className="font-headline-lg text-headline-lg lg:text-display-hero lg:font-display-hero uppercase text-outline tracking-tight leading-tight lg:leading-none mb-space-lg">
              Where to start
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant leading-relaxed" data-testid="result-intro">{RESULT.intro}</p>
          </div>
        </section>

        <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl bg-surface-container-low pt-space-2xl">
          <div className="max-w-4xl mx-auto flex flex-col gap-space-lg">
            <WellbeingWheel wheel={answers.q1} />

            <div className="bg-surface p-space-xl rounded-lg shadow-sm">
              <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-lg">My top 3 priorities</h2>
              <div className="flex flex-col gap-space-lg" data-testid="result-priorities">
                {priorities.map((area, i) => {
                  const rec = RECOMMENDATIONS[area] || {};
                  const challenge = challengeById(rec.challenge);
                  return (
                    <div key={area} className="pb-space-lg border-b border-surface-container last:border-0 last:pb-0" data-testid={`result-priority-${i + 1}`}>
                      <div className="flex items-center gap-space-sm mb-space-md">
                        <span className="w-8 h-8 rounded-full bg-surface-tint text-on-primary flex items-center justify-center font-label-md text-label-md font-bold">{i + 1}</span>
                        <h3 className="font-title-md text-title-md text-outline font-bold uppercase tracking-wide">{area}</h3>
                      </div>
                      {(rec.guides || []).length ? (
                        <GuideLinks slugs={rec.guides} titles={titles} testId={`result-guides-${i + 1}`} />
                      ) : rec.area_page ? (
                        <Link to={rec.area_page} className="inline-flex items-center gap-space-xs font-label-md text-label-md uppercase tracking-wider text-surface-tint font-bold hover:text-primary transition-colors">
                          <span>Explore this area of life</span>
                          <Icon name="arrow_forward" className="text-[18px]" />
                        </Link>
                      ) : null}
                      <div className="mt-space-md flex flex-col sm:flex-row sm:items-center gap-space-sm">
                        <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant font-bold">Starter habit</span>
                        <span className="font-body-md text-body-md text-outline">{rec.starter_habit}</span>
                      </div>
                      {challenge ? (
                        <p className="mt-space-xs font-body-sm text-body-sm text-on-surface-variant">Recommended challenge: {challenge.name}</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {wantsConditionGuides(answers) ? (
              <div className="bg-surface p-space-xl rounded-lg shadow-sm" data-testid="result-condition-guides">
                <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-sm">Understanding a health condition</h2>
                <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">{RESULT.condition_note}</p>
                <GuideLinks slugs={RESULT.condition_guides_if_q2_health_condition} titles={titles} testId="result-condition-list" />
              </div>
            ) : null}

            {needsSupport(answers) ? (
              <div className="flex flex-col gap-space-md" data-testid="result-support">
                <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{RESULT.support_if_q8_most_days}</p>
                <HelplineBox />
              </div>
            ) : null}

            {/* Save: nothing is stored until there is an account AND the box is ticked */}
            {saved ? null : (
              <form onSubmit={savePlan} className="bg-surface p-space-xl rounded-lg shadow-sm" data-testid="result-save">
                <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-sm">Save this to your personal space</h2>
                <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-lg">{RESULT.privacy}</p>

                {isAuthed ? null : (
                  <>
                    <div className="flex gap-space-sm mb-space-lg">
                      <button type="button" data-testid="result-mode-signup" onClick={() => setMode('signup')} className={mode === 'signup' ? primaryBtn : ghostBtn}>Create account</button>
                      <button type="button" data-testid="result-mode-login" onClick={() => setMode('login')} className={mode === 'login' ? primaryBtn : ghostBtn}>Log in</button>
                    </div>
                    <div className="flex flex-col gap-space-md mb-space-lg">
                      {mode === 'signup' ? (
                        <input
                          className="w-full h-11 px-space-md bg-surface-container-low rounded-lg border border-surface-container font-body-md text-body-md text-outline focus:outline-none focus:border-surface-tint"
                          placeholder="Your name" aria-label="Your name" required data-testid="result-name"
                          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                      ) : null}
                      <input
                        type="email" className="w-full h-11 px-space-md bg-surface-container-low rounded-lg border border-surface-container font-body-md text-body-md text-outline focus:outline-none focus:border-surface-tint"
                        placeholder="Email" aria-label="Email" required data-testid="result-email"
                        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                      <input
                        type="password" className="w-full h-11 px-space-md bg-surface-container-low rounded-lg border border-surface-container font-body-md text-body-md text-outline focus:outline-none focus:border-surface-tint"
                        placeholder="Password" aria-label="Password" required minLength={8} data-testid="result-password"
                        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                      />
                    </div>
                  </>
                )}

                <label className="flex items-start gap-space-sm mb-space-lg cursor-pointer">
                  <input
                    type="checkbox" checked={consent} data-testid="result-consent"
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#F028DF]"
                  />
                  <span className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{RESULT.signup_consent}</span>
                </label>

                {notice ? <p className="font-body-md text-body-md text-on-surface-variant mb-space-md" data-testid="result-notice">{notice}</p> : null}

                <div className="flex flex-col sm:flex-row gap-space-sm">
                  <button type="submit" disabled={busy} className={primaryBtn} data-testid="result-save-button">
                    <Icon name="check" className="text-[18px]" />
                    <span>{isAuthed ? 'Save to My Wellbeing Space' : mode === 'signup' ? 'Create my free account' : 'Log in and save'}</span>
                  </button>
                  <Link to="/resources" className={ghostBtn} data-testid="result-skip">Just show me the guides</Link>
                </div>
              </form>
            )}
          </div>
        </section>
      </div>
    );
  }

  // ----------------------------- questions -------------------------------- //
  return (
    <div className="flex flex-col w-full" data-testid="assessment-flow">
      <section className="w-full px-margin-mobile lg:px-margin pt-space-xl pb-space-2xl bg-surface relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-[420px] h-[420px] rounded-full bg-primary-container/50 blur-[120px] pointer-events-none -z-10" />
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-space-sm">
            <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold">START YOUR WELLBEING</span>
            <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant" data-testid="assessment-step">Question {step + 1} of {QUESTIONS.length}</span>
          </div>
          <Progress value={((step + 1) / QUESTIONS.length) * 100} className="h-1.5 mb-space-xl" data-testid="assessment-progress" />

          <h1 className="font-headline-md text-headline-md lg:text-headline-lg uppercase text-outline tracking-tight leading-tight mb-space-sm" data-testid="assessment-question">
            {question.text}
          </h1>
          {question.hint ? (
            <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg">{question.hint}</p>
          ) : null}
          {question.type === 'multi' ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-lg" data-testid="assessment-limit">Choose up to {question.max}.</p>
          ) : null}

          <div className="mt-space-lg flex flex-col gap-space-md">
            {question.type === 'sliders' ? (
              <div className="bg-surface-container-low p-space-lg rounded-lg flex flex-col gap-space-lg" data-testid="assessment-sliders">
                <div className="flex justify-between font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
                  <span>{question.min_label}</span>
                  <span>{question.max_label}</span>
                </div>
                {question.items.map((item) => (
                  <div key={item}>
                    <div className="flex items-center justify-between mb-space-xs">
                      <span className="font-title-md text-title-md text-outline font-bold">{item}</span>
                      <span className="font-label-md text-label-md text-surface-tint font-bold" data-testid={`slider-value-${item}`}>{answers.q1?.[item] ?? 5}</span>
                    </div>
                    <Slider
                      min={question.min}
                      max={question.max}
                      step={1}
                      value={[answers.q1?.[item] ?? 5]}
                      onValueChange={([v]) => setAnswers((a) => ({ ...a, q1: { ...a.q1, [item]: v } }))}
                      aria-label={item}
                      data-testid={`slider-${item}`}
                    />
                  </div>
                ))}
              </div>
            ) : null}

            {question.type === 'multi' || question.type === 'single' ? (
              <div className="flex flex-col gap-space-sm" data-testid="assessment-options">
                {question.options.map((option) => {
                  const current = answers[question.id];
                  const active = question.type === 'multi' ? (current || []).includes(option) : current === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      role={question.type === 'multi' ? 'checkbox' : 'radio'}
                      aria-checked={active}
                      data-testid={`option-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`}
                      onClick={() => (question.type === 'multi' ? toggleMulti(option) : setAnswer(option))}
                      className={optionBtn(active)}
                    >
                      <span className="flex items-center gap-space-sm">
                        <Icon
                          name={question.type === 'multi'
                            ? (active ? 'check_box' : 'check_box_outline_blank')
                            : (active ? 'radio_button_checked' : 'radio_button_unchecked')}
                          className={`text-[20px] ${active ? 'text-surface-tint' : 'text-on-surface-variant'}`}
                        />
                        <span>{option}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="mt-space-xl flex items-center justify-between gap-space-md">
            {step > 0 ? (
              <button type="button" onClick={() => setStep(step - 1)} className={ghostBtn} data-testid="assessment-back">
                <Icon name="arrow_back" className="text-[18px]" /> Back
              </button>
            ) : (
              <Link to="/" className={ghostBtn} data-testid="assessment-leave">
                <Icon name="arrow_back" className="text-[18px]" /> Home
              </Link>
            )}
            <button
              type="button"
              onClick={next}
              disabled={!isAnswered(question, answers)}
              className={primaryBtn}
              data-testid="assessment-next"
            >
              <span>{step + 1 === QUESTIONS.length ? 'See my snapshot' : 'Next'}</span>
              <Icon name="arrow_forward" className="text-[18px]" />
            </button>
          </div>
          <p className="mt-space-lg font-body-sm text-body-sm text-on-surface-variant">{RESULT.privacy}</p>
        </div>
      </section>
    </div>
  );
}
